import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Card as CardMaterial,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  createTheme,
  CardMedia,
  Tooltip,
  Box,
  Typography,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteEvento,
  getEventoById,
  postEvento,
  updateEvento,
} from "../../../../api/evento";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import MultiTabBlock from "../../../components/MultiTabBlock";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import Editor from "../../../components/editor/Editor";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import { Delete, Visibility } from "@material-ui/icons";
import { PictureAsPdf, Description, InsertDriveFile } from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { SERVER_URL } from "../../../../api";
import { buttonsStyle } from "../../../components/tables/table";
import { shallowEqual, useSelector } from "react-redux";
import { FlashOn } from "@material-ui/icons";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyEvento() {
  return {
    titulo: {},
    texto: {},
    ubicacion: "",
    imagenes: [],
    fechaIni: "",
    fechaFin: "",
    horaIni: "",
    horaFin: "",
    adjunto: "",
    url: "",
    enlace: {},
    tituloEnlace: {},
    descripcionEnlace: {},
    active: true,
  };
}

export default function EditEventoPage() {
  const [evento, setEvento] = useState(getEmptyEvento());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]); // Array for multiple images
  const [selectedFile, setSelectedFile] = useState(null); // For adjunto file
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const eventoId = useParams().id;
  const history = useHistory();
  const languages = useSelector((store) => store.authentication?.languages, shallowEqual);

  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    const fetch = async () => {
      if (!eventoId) {
        disableLoadingData();
        return;
      }
      getEventoById(eventoId)
        .then((res) => {
          if (res.status === 200) {
            const evento = res.data;
            // Convert dates to proper format for input fields
            if (evento.fechaIni) {
              evento.fechaIni = new Date(evento.fechaIni)
                .toISOString()
                .split("T")[0];
            }
            if (evento.fechaFin) {
              evento.fechaFin = new Date(evento.fechaFin)
                .toISOString()
                .split("T")[0];
            }
            setEvento(evento);
            disableLoadingData();
          }
        })
        .catch((error) => {
          alertError({ error: error, customMessage: "Could not get evento." });
          history.push("/eventos");
        });
    };
    fetch();
  }, [eventoId, disableLoadingData, history]);

  function saveEvento() {
    let saveEvento = { ...evento };

    // Convert date strings back to Date objects
    if (saveEvento.fechaIni) {
      saveEvento.fechaIni = new Date(saveEvento.fechaIni);
    }
    if (saveEvento.fechaFin) {
      saveEvento.fechaFin = new Date(saveEvento.fechaFin);
    }

    if (!eventoId) {
      postEvento(saveEvento, selectedImages, selectedFile)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Evento successfully created.",
            });
            history.push("/eventos");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save evento.",
          });
        });
    } else {
      updateEvento(eventoId, saveEvento, selectedImages, selectedFile)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/eventos");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element, lang) => (event) => {
    if (lang) {
      if (event.target.value === " ") return;

      if (!evento[element]) evento[element] = {};
      let newText = evento[element];
      newText[lang] = event.target.value;
      setEvento({ ...evento, [element]: newText });
    } else setEvento({ ...evento, [element]: event.target.value });
  };

  const handleChangeEditor = (element, lang, value) => {
    if (lang) {
      if (value === " ") return;
      if (!evento[element]) evento[element] = {};
      let newEvento = evento[element];
      newEvento[lang] = value;
      setEvento({ ...evento, [element]: newEvento });
    } else setEvento({ ...evento, [element]: value });
  };

  // Function to get file icon based on file type
  const getFileIcon = (fileName) => {
    if (!fileName) return <InsertDriveFile />;

    const extension = fileName
      .split(".")
      .pop()
      ?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <PictureAsPdf style={{ fontSize: 60, color: "#d32f2f" }} />;
      case "doc":
      case "docx":
        return <Description style={{ fontSize: 60, color: "#1976d2" }} />;
      case "xls":
      case "xlsx":
        return <Description style={{ fontSize: 60, color: "#388e3c" }} />;
      case "ppt":
      case "pptx":
        return <Description style={{ fontSize: 60, color: "#f57c00" }} />;
      case "txt":
        return <Description style={{ fontSize: 60, color: "#616161" }} />;
      default:
        return <InsertDriveFile style={{ fontSize: 60, color: "#757575" }} />;
    }
  };

  // Function to get file name
  const getFileName = (file, fileURL) => {
    if (file) {
      return file.name;
    }
    if (fileURL) {
      return fileURL.split("/").pop() || "Archivo";
    }
    return "Archivo";
  };

  // Handle removing an image from the array
  const handleRemoveImage = (index) => {
    const newImages = [...selectedImages];
    newImages.splice(index, 1);
    setSelectedImages(newImages);

    // Also remove from evento.imagenes if editing existing event
    if (evento.imagenes && evento.imagenes.length > 0) {
      const newEventoImages = [...evento.imagenes];
      if (index < newEventoImages.length) {
        newEventoImages.splice(index, 1);
        setEvento({ ...evento, imagenes: newEventoImages });
      }
    }
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Back Button */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Save and Delete Buttons */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMultilanguageTabContent = (lang) => {
    return (
      <>
        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <TextField
              id={`titulo`}
              label="Título"
              value={
                evento.titulo && evento.titulo[lang] ? evento.titulo[lang] : ""
              }
              onChange={handleChange("titulo", lang)}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              required
            />
          </div>
          <div className="col-md-6">
            <TextField
              id={`url`}
              label="URL"
              value={evento.url || ""}
              onChange={handleChange("url")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </div>
        </div>

        <div className="d-flex align-text-center">
          <div className="col-md-12">
            <label>Descripción del Evento</label>
            <Editor
              body={(evento.texto && evento.texto[lang]) || ""}
              setBody={(new_body) =>
                handleChangeEditor("texto", lang, new_body)
              }
              className="max-height"
              lang={lang}
              placeholder={"Ingrese la descripción del evento aquí..."}
            />
          </div>
        </div>

        <div className="d-flex align-text-center">
          <div className="col-md-12">
            <TextField
              id={`ubicacion`}
              label="Ubicación"
              value={evento.ubicacion || ""}
              onChange={handleChange("ubicacion")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </div>
        </div>

        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <TextField
              id={`fechaIni`}
              label="Fecha de Inicio"
              type="date"
              value={evento.fechaIni || ""}
              onChange={handleChange("fechaIni")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              required
            />
          </div>
          <div className="col-md-6">
            <TextField
              id={`fechaFin`}
              label="Fecha de Fin"
              type="date"
              value={evento.fechaFin || ""}
              onChange={handleChange("fechaFin")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
              required
            />
          </div>
        </div>

        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <TextField
              id={`horaIni`}
              label="Hora de Inicio"
              type="time"
              value={evento.horaIni || ""}
              onChange={handleChange("horaIni")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </div>
          <div className="col-md-6">
            <TextField
              id={`horaFin`}
              label="Hora de Fin"
              type="time"
              value={evento.horaFin || ""}
              onChange={handleChange("horaFin")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              variant="outlined"
            />
          </div>
        </div>

        {/* Images Section */}
        <div className="col-12 mt-4">
          <Typography variant="h6" gutterBottom>
            Imágenes del Evento
          </Typography>

          {/* Display existing and selected images */}
          <div className="row">
            {/* Show existing images from server */}
            {evento.imagenes &&
              evento.imagenes.map((imageUrl, index) => (
                <div key={`existing-${index}`} className="col-md-4 mb-3">
                  <CardMaterial>
                    <CardMedia
                      component="img"
                      alt={`Evento image ${index + 1}`}
                      style={{
                        objectFit: "cover",
                        width: "100%",
                        height: "200px",
                      }}
                      image={`${SERVER_URL}/${imageUrl}`}
                    />
                    <div className="p-2">
                      <Tooltip title="Preview">
                        <Button
                          size="small"
                          onClick={() =>
                            setOpenPreviewDialog(`existing-${index}`)
                          }
                          style={{ marginRight: "10px" }}
                        >
                          <Visibility />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          size="small"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <Delete />
                        </Button>
                      </Tooltip>
                    </div>
                  </CardMaterial>
                  <PreviewDialog
                    title="Preview Imagen"
                    open={openPreviewDialog === `existing-${index}`}
                    setOpen={setOpenPreviewDialog}
                    src={`${SERVER_URL}/${imageUrl}`}
                  />
                </div>
              ))}

            {/* Show newly selected images */}
            {selectedImages.map((image, index) => (
              <div key={`new-${index}`} className="col-md-4 mb-3">
                <CardMaterial>
                  <CardMedia
                    component="img"
                    alt={`New image ${index + 1}`}
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "200px",
                    }}
                    image={URL.createObjectURL(image)}
                  />
                  <div className="p-2">
                    <Tooltip title="Preview">
                      <Button
                        size="small"
                        onClick={() => setOpenPreviewDialog(`new-${index}`)}
                        style={{ marginRight: "10px" }}
                      >
                        <Visibility />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Button
                        size="small"
                        onClick={() => {
                          const newImages = [...selectedImages];
                          newImages.splice(index, 1);
                          setSelectedImages(newImages);
                        }}
                      >
                        <Delete />
                      </Button>
                    </Tooltip>
                  </div>
                </CardMaterial>
                <PreviewDialog
                  title="Preview New Image"
                  open={openPreviewDialog === `new-${index}`}
                  setOpen={setOpenPreviewDialog}
                  file={image}
                  src={URL.createObjectURL(image)}
                />
              </div>
            ))}
          </div>

          {/* Upload Images Button */}
          <label htmlFor="upload-images" className="ml-0 my-2">
            <input
              ref={imageInputRef}
              style={{ display: "none" }}
              id="upload-images"
              name="upload-images"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                setSelectedImages([...selectedImages, ...files]);
              }}
            />
            <Button color="secondary" component="span" variant="outlined">
              Añadir Imágenes
            </Button>
          </label>
        </div>

        {/* Attachment File Section */}
        <div className="col-12 mt-4">
          <Typography variant="h6" gutterBottom>
            Adjunto
          </Typography>

          {(selectedFile || evento.adjunto) && (
            <CardMaterial style={{ marginBottom: "20px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: 3,
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  backgroundColor: "#f9f9f9",
                  minHeight: "150px",
                  justifyContent: "center",
                }}
              >
                {getFileIcon(getFileName(selectedFile, evento.adjunto))}
                <Typography
                  variant="body1"
                  sx={{
                    marginTop: 1,
                    textAlign: "center",
                    wordBreak: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  {getFileName(selectedFile, evento.adjunto)}
                </Typography>
                {selectedFile && (
                  <Typography variant="caption" color="textSecondary">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                )}
              </Box>
            </CardMaterial>
          )}

          <label htmlFor="upload-file" className="ml-0 my-2">
            <input
              ref={fileInputRef}
              style={{ display: "none" }}
              id="upload-file"
              name="upload-file"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Validate file size (max 10MB)
                  const maxSize = 10 * 1024 * 1024;
                  if (file.size > maxSize) {
                    alert(
                      "El archivo es demasiado grande. El tamaño máximo permitido es 10MB."
                    );
                    return;
                  }
                  setSelectedFile(file);
                }
              }}
            />
            <Button
              style={{ marginRight: "15px" }}
              color="secondary"
              component="span"
              variant="outlined"
            >
              {selectedFile || evento.adjunto
                ? "Cambiar Adjunto"
                : "Subir Adjunto"}
            </Button>
          </label>

          {(selectedFile || evento.adjunto) && (
            <>
              <Tooltip title="Vista previa">
                <Button
                  size="small"
                  onClick={() => {
                    const url = selectedFile
                      ? URL.createObjectURL(selectedFile)
                      : `${SERVER_URL}/${evento.adjunto}`;

                    if (
                      (selectedFile &&
                        selectedFile.type === "application/pdf") ||
                      (!selectedFile &&
                        evento.adjunto &&
                        evento.adjunto.endsWith(".pdf"))
                    ) {
                      window.open(url, "_blank");
                    } else {
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = getFileName(selectedFile, evento.adjunto);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                  style={{
                    ...buttonsStyle,
                    marginRight: "15px",
                  }}
                >
                  <Visibility />
                </Button>
              </Tooltip>

              <Tooltip title="Eliminar">
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedFile(null);
                    setEvento({ ...evento, adjunto: "" });
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  <Delete />
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </>
    );
  };

  const renderContentDatosGenerales = () => {
    return (
      <>
        <MultilanguageTabBlock
          multilanguageTabContent={renderMultilanguageTabContent}
        />
      </>
    );
  };

  const renderMultilanguageTabContentEnlace = (lang) => {
    return (
      <>
        <TextField
          id={`enlace`}
          label="Url amigable"
          value={
            evento.enlace && evento.enlace[lang] ? evento.enlace[lang] : ""
          }
          onChange={handleChange("enlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`tituloEnlace`}
          label="Titulo SEO"
          value={
            evento.tituloEnlace && evento.tituloEnlace[lang]
              ? evento.tituloEnlace[lang]
              : ""
          }
          onChange={handleChange("tituloEnlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`descripcionEnlace`}
          label="Descripción SEO"
          value={
            evento.descripcionEnlace && evento.descripcionEnlace[lang]
              ? evento.descripcionEnlace[lang]
              : ""
          }
          onChange={handleChange("descripcionEnlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
      </>
    );
  };

  // Función para limpiar HTML y convertir entidades HTML a texto
  const stripHTML = (html) => {
    if (!html) return "";
    
    // Crear un elemento temporal para extraer el texto
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    
    // Obtener el texto sin HTML
    let text = tmp.textContent || tmp.innerText || "";
    
    // Limpiar espacios múltiples y saltos de línea
    text = text.replace(/\s+/g, " ").trim();
    
    return text;
  };

  // Función para generar URL amigable desde un texto
  const generateSlug = (text) => {
    if (!text) return "";
    
    // Limpiar HTML primero
    let slug = stripHTML(text);
    
    // Convertir a minúsculas
    slug = slug.toLowerCase();
    
    // Reemplazar caracteres especiales y acentos
    slug = slug
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Eliminar diacríticos
      .replace(/[^a-z0-9\s-]/g, "") // Eliminar caracteres especiales
      .replace(/\s+/g, "-") // Reemplazar espacios con guiones
      .replace(/-+/g, "-") // Reemplazar múltiples guiones con uno solo
      .replace(/^-|-$/g, ""); // Eliminar guiones al inicio y final
    
    return slug;
  };

  const renderContentEnlace = () => {
    const handleGenerateSEO = async () => {
      if (!languages || languages.length === 0) {
        alertError({
          customMessage: "No hay idiomas disponibles para generar el SEO",
        });
        return;
      }

      // Obtener información del evento para generar el SEO
      const eventoInfo = {
        titulo: (evento && evento.titulo) ? evento.titulo : {},
        texto: (evento && evento.texto) ? evento.texto : {},
      };

      try {
        // Mostrar mensaje de carga
        alertSuccess({
          title: "Generando SEO...",
          customMessage: "Por favor espera mientras se genera el SEO para todos los idiomas",
        });

        // Generar SEO para cada idioma
        const seoData = {};
        
        for (const lang of languages) {
          const langCode = lang.isocode;
          if (!langCode) continue;

          // Preparar contexto del evento en el idioma actual con validaciones
          const titulo = (eventoInfo.titulo && eventoInfo.titulo[langCode]) 
            ? eventoInfo.titulo[langCode] 
            : (eventoInfo.titulo && eventoInfo.titulo["es"]) 
              ? eventoInfo.titulo["es"] 
              : "";
          const texto = (eventoInfo.texto && eventoInfo.texto[langCode]) 
            ? eventoInfo.texto[langCode] 
            : (eventoInfo.texto && eventoInfo.texto["es"]) 
              ? eventoInfo.texto["es"] 
              : "";

          // Limpiar HTML de los textos
          const tituloLimpio = stripHTML(titulo);
          const textoLimpio = stripHTML(texto);

          // Generar título SEO (máximo 60 caracteres)
          const tituloSEO = tituloLimpio && tituloLimpio.length > 0
            ? (tituloLimpio.length > 60 ? tituloLimpio.substring(0, 57) + "..." : tituloLimpio)
            : `Evento ${eventoId || ""}`;

          // Generar descripción SEO (máximo 160 caracteres)
          let descripcionSEO = "";
          if (textoLimpio && textoLimpio.length > 0) {
            descripcionSEO = textoLimpio.length > 160 ? textoLimpio.substring(0, 157) + "..." : textoLimpio;
          }
          
          if (!descripcionSEO || descripcionSEO.length === 0) {
            descripcionSEO = `Información sobre el evento ${tituloSEO}`;
          }

          // Generar URL amigable
          const urlAmigable = generateSlug(tituloLimpio || tituloSEO);

          seoData[langCode] = {
            enlace: urlAmigable,
            tituloEnlace: tituloSEO,
            descripcionEnlace: descripcionSEO,
          };
        }

        // Actualizar el evento con el SEO generado
        setEvento((prevEvento) => ({
          ...prevEvento,
          enlace: {
            ...prevEvento.enlace,
            ...Object.fromEntries(
              Object.entries(seoData).map(([lang, data]) => [lang, data.enlace])
            ),
          },
          tituloEnlace: {
            ...prevEvento.tituloEnlace,
            ...Object.fromEntries(
              Object.entries(seoData).map(([lang, data]) => [lang, data.tituloEnlace])
            ),
          },
          descripcionEnlace: {
            ...prevEvento.descripcionEnlace,
            ...Object.fromEntries(
              Object.entries(seoData).map(([lang, data]) => [lang, data.descripcionEnlace])
            ),
          },
        }));

        alertSuccess({
          title: "SEO generado!",
          customMessage: `Se ha generado el SEO para ${Object.keys(seoData).length} idiomas`,
        });
      } catch (error) {
        console.error("Error generando SEO:", error);
        alertError({
          error: error,
          customMessage: "No se ha podido generar el SEO",
        });
      }
    };

    return (
      <>
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="contained"
            onClick={handleGenerateSEO}
            startIcon={<FlashOn />}
            style={{
              backgroundColor: "#9c27b0",
              color: "#fff",
            }}
          >
            GENERAR
          </Button>
        </div>
        <MultilanguageTabBlock
          multilanguageTabContent={renderMultilanguageTabContentEnlace}
        />
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/eventos"),
            onSave: saveEvento,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!eventoId,
            setObj: setEvento,
            obj: evento,
          })}
          <CardHeader title="Editar evento"></CardHeader>
          <CardBody>
            <MultiTabBlock
              tabs={[
                {
                  enable: true,
                  title: "Datos Generales",
                  renderContent: renderContentDatosGenerales,
                },
                {
                  enable: true,
                  title: "SEO",
                  renderContent: renderContentEnlace,
                },
              ]}
            />

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={evento.active || false}
                    onChange={() =>
                      setEvento({ ...evento, active: !evento.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
            </div>
          </CardBody>
        </Card>
        {eventoId && (
          <>
            <ConfirmDialog
              title={"¿Estás seguro de que quieres eliminar este evento?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteEvento(eventoId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Evento deleted successfully",
                      });
                      history.push("/eventos");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete evento.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
